// CRM 2.0 · M12 Template Builder · Page setup (DC-203): margins on all sides, mirrored for binding, and how tables
// fit (DC-68 table tidy). Filled in from the Word file; saved per version; the Preview PDF uses it at once.
// The approver sees the Word file's values next to the new ones.
import React, { useEffect, useMemo, useState } from "react";
import cls from "./Templates.module.css";
import { savePageSetup } from "../../Services/Templates";
import { errorTost, successTost } from "../../Services/Toast";

const QR_EDGE_IN = 6 / 72; // the gap kept around a QR (backend pageSetup.js)

function wordValues(scan, settings) {
  const m = scan?.margins || {};
  return {
    topIn: m.topIn, bottomIn: m.bottomIn, insideIn: m.leftIn, outsideIn: m.rightIn, mirrored: !!m.mirrored,
    tableMode: settings?.tableWidthMode === "INSIDE_MARGINS" ? "FIT_MARGINS" : "COMMON",
  };
}

const TABLES = { FIT_MARGINS: "Fit inside the margins", COMMON: "One common width" };
const inch = (v) => (v === null || v === undefined || v === "" ? "—" : `${Number(v).toFixed(2)} in`);

function PageSetup({ versionId, version, settings, canEdit, onSaved }) {
  const word = useMemo(() => wordValues(version.scan, settings), [version.scan, settings]);
  const saved = version.pageSetup || null;
  const [form, setForm] = useState({ ...word, ...(saved || {}) });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm({ ...word, ...(saved || {}) }); }, [word, saved]);

  const kind = (settings?.documentKinds || []).find((k) => k.key === version.templateTypeKey);
  const qrMin = kind?.hasQr ? Math.round((Number(settings?.qrSizeMm || 18) / 25.4 + 2 * QR_EDGE_IN) * 100) / 100 : null;
  const page = version.scan?.pageSize || {};
  const cap = Number(settings?.tableCommonWidthEdgeCapIn ?? 0.6);
  const names = form.mirrored ? { insideIn: "Inside", outsideIn: "Outside" } : { insideIn: "Left", outsideIn: "Right" };
  const textW = page.widthIn ? page.widthIn - Number(form.insideIn) - Number(form.outsideIn) : null;
  const textH = page.heightIn ? page.heightIn - Number(form.topIn) - Number(form.bottomIn) : null;
  const vertical = settings?.qrPosition === "TOP" ? "topIn" : "bottomIn";
  const tooSmall = qrMin ? ["outsideIn", vertical].filter((k) => Number(form[k]) < qrMin) : [];
  const changed = (k) => saved && String(saved[k]) !== String(word[k]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save(body, message) {
    setBusy(true);
    try {
      const r = await savePageSetup(versionId, body);
      onSaved(r);
      successTost(message);
    } catch (e) {
      errorTost(e.message);
    } finally {
      setBusy(false);
    }
  }

  const rows = [
    ["topIn", "Top"], ["bottomIn", "Bottom"], ["insideIn", names.insideIn], ["outsideIn", names.outsideIn],
  ];

  return (
    <div className={cls.panel}>
      <h2 className={cls.panelTitle}>
        Page setup <span className={cls.count}>· {saved ? `changed by ${saved.byName || "Legal"}` : "as in the Word file"} · the Preview PDF uses it straight away</span>
      </h2>
      {canEdit ? (
        <>
          <div className={cls.setupGrid}>
            {rows.map(([k, label]) => (
              <div key={k}>
                <label className={cls.lbl} htmlFor={`ps-${k}`}>{label} margin (in)</label>
                <input id={`ps-${k}`} className={cls.inp} type="number" step="0.05" min="0.3" max="3" value={form[k] ?? ""}
                  onChange={(e) => set(k, e.target.value)} />
                <span className={cls.hint}>Word file: {inch(word[k])}</span>
              </div>
            ))}
          </div>
          <label className={cls.radio} style={{ marginTop: "1.2rem" }}>
            <input type="checkbox" checked={!!form.mirrored} onChange={(e) => set("mirrored", e.target.checked)} />
            <span><b>Mirrored for binding</b><br /><span className={cls.hint}>Inside and outside swap on even pages (Word file: {word.mirrored ? "on" : "off"})</span></span>
          </label>
          <p className={cls.lbl} style={{ marginTop: "1.2rem" }}>Tables</p>
          <div className={cls.setupGrid}>
            {Object.entries(TABLES).map(([k, t]) => (
              <label key={k} className={`${cls.radio} ${form.tableMode === k ? cls.radioOn : ""}`}>
                <input type="radio" name="tableMode" checked={form.tableMode === k} onChange={() => set("tableMode", k)} />
                <span><b>{t}</b><br /><span className={cls.hint}>
                  {k === "FIT_MARGINS" ? "Every table as wide as the text" : `Every table as wide as the widest one, at least ${cap} in from the paper edge`}
                </span></span>
              </label>
            ))}
          </div>
          <p className={cls.hint} style={{ marginTop: "1.2rem" }}>
            Text area {textW ? textW.toFixed(2) : "—"} × {textH ? textH.toFixed(2) : "—"} in on {page.name || ""} {page.widthIn} × {page.heightIn} in.
            {qrMin ? ` The ${form.mirrored ? "outside" : "right"} and ${vertical === "topIn" ? "top" : "bottom"} margins need at least ${qrMin} in for the QR.` : ""}
          </p>
          {tooSmall.length > 0 && <div className={cls.blockBox}>Too small for the QR: {tooSmall.map((k) => (k === "outsideIn" ? names.outsideIn : k === "topIn" ? "Top" : "Bottom")).join(" and ")} margin (at least {qrMin} in).</div>}
          <div style={{ display: "flex", gap: "1.2rem", justifyContent: "flex-end", marginTop: "1.2rem" }}>
            {saved && <button className={cls.btn} disabled={busy} onClick={() => save({ reset: true }, "Back to the Word file's page setup")}>Use the Word file's values</button>}
            <button className={`${cls.btn} ${cls.primary}`} disabled={busy || tooSmall.length > 0}
              onClick={() => save({ ...form, topIn: Number(form.topIn), bottomIn: Number(form.bottomIn), insideIn: Number(form.insideIn), outsideIn: Number(form.outsideIn) }, "Page setup saved")}>
              {busy ? "Saving…" : "Save page setup"}
            </button>
          </div>
        </>
      ) : (
        <div className={cls.tblw}>
          <table className={cls.tbl} style={{ minWidth: "48rem" }}>
            <thead><tr><th>Setting</th><th>Word file</th><th>Used for the print</th></tr></thead>
            <tbody>
              {rows.map(([k, label]) => (
                <tr key={k}><td>{label} margin</td><td>{inch(word[k])}</td><td className={changed(k) ? cls.main : ""}>{inch((saved || word)[k])}{changed(k) ? " · changed" : ""}</td></tr>
              ))}
              <tr><td>Mirrored for binding</td><td>{word.mirrored ? "On" : "Off"}</td><td className={changed("mirrored") ? cls.main : ""}>{(saved || word).mirrored ? "On" : "Off"}{changed("mirrored") ? " · changed" : ""}</td></tr>
              <tr><td>Tables</td><td>{TABLES[word.tableMode]}</td><td className={changed("tableMode") ? cls.main : ""}>{TABLES[(saved || word).tableMode]}{changed("tableMode") ? " · changed" : ""}</td></tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PageSetup;
